import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
} from "@mui/material";
import { TabulatorFull as Tabulator } from "tabulator-tables";
import Q8Breadcrumbs from "../components/Q8Breadcrumbs";
import Q8Button from "../components/Q8Button";
import Q8SnackBar from "../components/Q8SnackBar";
import {
  Add as AddIcon,
  FileDownload as ExportIcon,
  FilterList as FilterIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import "tabulator-tables/dist/css/tabulator.min.css";
import "../styles/tabulator.css";
import * as XLSX from "xlsx";
import UserService from "../service/userService";

const ROLE_MAPPINGS = {
  Admin: 3,
  Validator: 2,
  Coordinator: 1,
};

const ROLE_NAMES = {
  3: "Admin",
  2: "Validator",
  1: "Coordinator",
};

const UserManagement = () => {
  const tableRef = useRef(null);
  const [table, setTable] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [filtersActive, setFiltersActive] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "default",
  });
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    givenName: "",
    familyName: "",
  });

  const showNotification = (message, type = "default") => {
    setSnackbar({ open: true, message, type });
  };

  const fetchUsers = async () => {
    try {
      const data = await UserService.getUsers();
      if (table) {
        table.setData(data);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      let errorMessage = "Error loading users";

      // Handle different error formats
      if (error.detail) {
        errorMessage = error.detail;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      }

      showNotification(errorMessage, "error");
    }
  };

  const handleDownloadExcel = () => {
    if (table) {
      const data = table.getData();
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, "Users Data");
      XLSX.writeFile(wb, "Users_data.xlsx");
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setSelectedUser(user);
      setFormData({
        email: user.email,
        name: user.name,
        givenName: user.givenName,
        familyName: user.familyName,
      });
      // Prendi solo il primo ruolo se ce ne sono più di uno
      setSelectedRoles(
        user.userRoles && user.userRoles.length > 0
          ? [user.userRoles[0].roleName]
          : []
      );
    } else {
      setSelectedUser(null);
      setFormData({
        email: "",
        name: "",
        givenName: "",
        familyName: "",
      });
      setSelectedRoles([]);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setFormData({
      email: "",
      name: "",
      givenName: "",
      familyName: "",
    });
    setSelectedRoles([]);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRoleChange = (event) => {
    const role = event.target.value;
    // Sostituisci il ruolo precedente invece di aggiungerne uno nuovo
    setSelectedRoles([role]);
  };

  const handleDeleteUser = async (userId) => {
    try {
      await UserService.deleteUser(userId);
      showNotification("User deleted successfully", "success");
      setDeleteDialog({ open: false, user: null });
      fetchUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
      let errorMessage = "Error deleting user";

      // Handle different error formats
      if (error.detail) {
        errorMessage = error.detail;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      }

      showNotification(errorMessage, "error");
    }
  };

  const validateForm = () => {
    // Validazione per nuovi utenti
    if (!selectedUser) {
      if (!formData.email || !formData.email.trim()) {
        return false;
      }

      if (!/\S+@\S+\.\S+/.test(formData.email)) {
        showNotification("Email is invalid", "error");
        return false;
      }

      if (!formData.name || !formData.name.trim()) {
        return false;
      }
    }

    // Validazione per tutti gli utenti (nuovi e in modifica)
    if (selectedRoles.length === 0) {
      showNotification("Selezionare un ruolo.", "warning");
      return false;
    }

    return true;
  };

  const handleAddNew = async () => {
    try {
      if (!validateForm()) {
        return; // Esci se la validazione fallisce
      }

      if (selectedUser) {
        // Converti il nome del ruolo in ID (un solo ruolo è consentito)
        const roleId = ROLE_MAPPINGS[selectedRoles[0]];

        // Aggiorna il ruolo dell'utente
        await UserService.updateRoles({
          userId: selectedUser.id,
          roleIds: [roleId],
        });
        showNotification("User role updated successfully", "success");
      } else {
        // Crea un nuovo utente con un solo ruolo
        const userRole = {
          roleId: ROLE_MAPPINGS[selectedRoles[0]],
          roleName: selectedRoles[0],
        };

        const userData = {
          email: formData.email,
          name: formData.name,
          givenName: formData.givenName,
          familyName: formData.familyName,
          userRoles: [userRole],
        };

        await UserService.createUser(userData);
        showNotification("User created successfully", "success");
      }
      handleCloseDialog();
      fetchUsers();
    } catch (error) {
      console.error("Error:", error);
      let errorMessage = "Error processing request";

      // Handle different error formats
      if (error.detail) {
        errorMessage = error.detail;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      }

      showNotification(errorMessage, "error");
    }
  };

  // Fix for ResizeObserver loop error
  useEffect(() => {
    // Function to handle the ResizeObserver error
    const handleError = (event) => {
      if (event.message && event.message.includes("ResizeObserver loop")) {
        // Prevent the error from appearing in console
        event.stopImmediatePropagation();
      }
    };

    // Add event listener for error
    window.addEventListener("error", handleError, true);

    // Clean up
    return () => {
      window.removeEventListener("error", handleError, true);
    };
  }, []);

  useEffect(() => {
    if (table) {
      const tableElement = table.element;
      tableElement.classList.add("hide-filters");
    }
  }, [table]);

  useEffect(() => {
    if (tableRef.current && !table) {
      const columns = [
        {
          title: "Nome e Cognome",
          field: "name",
          sorter: "string",
          headerFilter: "input",
        },
        {
          title: "Email",
          field: "email",
          sorter: "string",
          headerFilter: "input",
        },
        {
          title: "Nome",
          field: "givenName",
          sorter: "string",
          headerFilter: "input",
        },
        {
          title: "Cognome",
          field: "familyName",
          sorter: "string",
          headerFilter: "input",
        },
        {
          title: "Ruoli",
          field: "userRoles",
          headerFilter: "input",
          formatter: (cell) => {
            const roles = cell.getValue();
            if (Array.isArray(roles) && roles.length > 0) {
              const roleColors = {
                Admin: "#dc3545",
                Validator: "#198754",
                Coordinator: "#0d6efd",
              };

              const sortedRoles = [...roles].sort((a, b) => {
                const roleOrder = { Admin: 1, Validator: 2, Coordinator: 3 };
                return roleOrder[a.roleName] - roleOrder[b.roleName];
              });

              return sortedRoles
                .map((role) => {
                  let roleName = "";

                  switch (role.roleName) {
                    case "Coordinator":
                      roleName = "Coordinatore";
                      break;
                    case "Validator":
                      roleName = "Validatore";
                      break;
                    case "Admin":
                      roleName = "Amministratore";
                      break;
                  }

                  return `<span class="role-badge" style="
                  background-color: ${roleColors[role.roleName]};
                  color: white;
                  padding: 2px 8px;
                  border-radius: 12px;
                  margin: 0 2px;
                  font-size: 0.85em;
                ">${roleName}</span>`;
                })
                .join(" ");
            }
            return "<span style='color: #6c757d;'>Nessun ruolo</span>";
          },
          headerFilterFunc: (headerValue, rowValue) => {
            if (!rowValue || !Array.isArray(rowValue)) return false;

            if (!headerValue) return false;

            const searchTerm = headerValue.toLowerCase();

            return rowValue.some((role) =>
              role.roleName.toLowerCase().includes(searchTerm)
            );
          },
          sorter: (a, b) => {
            const roleOrder = { Admin: 1, Validator: 2, Coordinator: 3 };

            const roleA =
              a && Array.isArray(a) && a.length > 0 ? a[0].roleName : "";
            const roleB =
              b && Array.isArray(b) && b.length > 0 ? b[0].roleName : "";

            return roleOrder[roleA] - roleOrder[roleB];
          },
          width: 250,
        },
      ];

      try {
        // Add a small delay before initializing Tabulator to avoid ResizeObserver issues
        setTimeout(() => {
          const newTable = new Tabulator(tableRef.current, {
            columns,
            layout: "fitColumns",
            pagination: "local",
            paginationSize: 100,
            paginationSizeSelector: [10, 25, 50, 100],
            height: "100%", // Consider using specific height instead of 100%
            placeholder: "No Users Available",
            initialSort: [{ column: "name", dir: "asc" }],
            headerFilterLiveFilter: true,
            headerFilterPlaceholder: "Filter...",
            // Add responsiveLayout option
            responsiveLayout: "hide",
            // Add redraw flag
            autoResize: false, // Disable automatic resizing
          });

          setTable(newTable);
        }, 100);
      } catch (error) {
        console.error("Error initializing table:", error);
      }
    }
  }, []);

  useEffect(() => {
    if (table) {
      fetchUsers();
    }

    // Clean up function to destroy table on unmount
    return () => {
      if (table) {
        try {
          table.destroy();
        } catch (error) {
          console.error("Error destroying table:", error);
        }
      }
    };
  }, [table]);

  // CSS per i pulsanti moderni
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .modern-button {
        border: none;
        border-radius: 4px;
        width: 50px;
        height: 32px;
        margin: 0 4px;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
        box-shadow: 0 2px 5px rgba(0,0,0,0.1);
      }
      
      .modern-button.edit {
        background-color: #4285F4;
        color: white;
      }
      
      .modern-button.delete {
        background-color: #EA4335;
        color: white;
      }
      
      .modern-button:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0,0,0,0.15);
      }
      
      .modern-button:active {
        transform: translateY(0);
        box-shadow: 0 1px 3px rgba(0,0,0,0.1);
      }
      
      .modern-button .material-icons {
        font-size: 14px;
        font-family: 'Material Icons';
      }
      
      /* Fallback per Material Icons */
      .modern-button.edit .material-icons:before {
        content: '✎';
      }
      
      .modern-button.delete .material-icons:before {
        content: '🗑';
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Q8Breadcrumbs
        links={[{ label: "Gestione Utenti", href: "/userManagement" }]}
        variant="primary"
      />

      <Box sx={{ mt: 3, mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
        <Typography variant="h4" sx={{ color: "primary.main" }}>
          Gestione Utenti
        </Typography>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Q8Button
            variant="secondary"
            label="Esporta Excel"
            icon={<ExportIcon />}
            onClick={handleDownloadExcel}
            size="small"
          />
          <Q8Button
            variant="tertiary"
            label={filtersActive ? "Nascondi Filtri" : "Mostra Filtri"}
            icon={<FilterIcon />}
            onClick={() => {
              if (table) {
                const newState = !filtersActive;
                setFiltersActive(newState);
                const tableElement = table.element;
                if (newState) {
                  tableElement.classList.remove("hide-filters");
                } else {
                  tableElement.classList.add("hide-filters");
                  table.clearHeaderFilter();
                }
              }
            }}
            size="small"
          />
        </Box>
      </Box>

      <Card
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <div
          ref={tableRef}
          style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}
        ></div>
      </Card>

      {/* Dialog for add/edit user */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{selectedUser ? "Modifica Utente" : "Aggiungi Nuovo Utente"}</DialogTitle>
        <DialogContent>
          <Box
            component="form"
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 2,
              py: 2,
            }}
          >
            <TextField
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              fullWidth
              disabled={!!selectedUser}
              required
              error={!formData.email && !selectedUser}
              helperText={
                !formData.email && !selectedUser ? "Campo obbligatorio" : ""
              }
            />
            <TextField
              label="NomeCognome"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              fullWidth
              disabled={!!selectedUser}
              required
              error={!formData.name && !selectedUser}
              helperText={
                !formData.name && !selectedUser ? "Campo obbligatorio" : ""
              }
            />
            <TextField
              label="Nome"
              name="givenName"
              value={formData.givenName}
              onChange={handleInputChange}
              fullWidth
              disabled={!!selectedUser}
            />
            <TextField
              label="Cognome"
              name="familyName"
              value={formData.familyName}
              onChange={handleInputChange}
              fullWidth
              disabled={!!selectedUser}
            />
            <Box sx={{ gridColumn: "span 2" }}>
              <TextField
                select
                label="Role"
                name="role"
                value={selectedRoles.length > 0 ? selectedRoles[0] : ""}
                onChange={handleRoleChange}
                fullWidth
                required
                error={selectedRoles.length === 0}
                helperText={
                  selectedRoles.length === 0 ? "Campo obbligatorio" : ""
                }
              >
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="Validator">Validator</MenuItem>
                <MenuItem value="Coordinator">Coordinator</MenuItem>
              </TextField>
            </Box>
            {/* Il box per i chip è stato rimosso poiché non necessario con un solo ruolo */}
          </Box>
        </DialogContent>
        <DialogActions>
          <Q8Button
            variant="secondary"
            label="Annulla"
            onClick={handleCloseDialog}
          />
          <Q8Button
            variant="primary"
            label={selectedUser ? "Salva" : "Aggiungi"}
            onClick={handleAddNew}
          />
        </DialogActions>
      </Dialog>

      {/* Dialog for delete confirmation */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, user: null })}
      >
        <DialogTitle>Conferma Eliminazione</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sei sicuro di voler eliminare l'utente "{deleteDialog.user?.email}
            "? Quest'azione non può essere annullata.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Q8Button
            variant="secondary"
            label="Annulla"
            onClick={() => setDeleteDialog({ open: false, user: null })}
          />
          <Q8Button
            variant="primary"
            label="Elimina"
            onClick={() => handleDeleteUser(deleteDialog.user?.id)}
          />
        </DialogActions>
      </Dialog>

      <Q8SnackBar
        open={snackbar.open}
        type={snackbar.type ?? "default"}
        title="Notification"
        message={snackbar.message}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
};

export default UserManagement;
